import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { AnalysisDrawerRight, DrawersProps, FindViewpointsToolState } from '@/types';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ViewpointsTable } from './ViewpointsTable';
import { primary } from '@/theme/colors';
import * as moduleState from '@/components/Analysis/modules/findViewpointsTool/state';
import * as state from '@/components/Analysis/state';
import { toggleValue } from '@/utils';
import { useFindViewpointsTool } from '@/components/Analysis/modules/findViewpointsTool';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Number3 } from '@abyss/3d-viewer';
import SearchIcon from '@mui/icons-material/Search';
import { ActionButton, ImageThumbnailHeader, ImageThumbnailActionButtons } from './styles';

// small voxel key has integer coordinates (voxel size step)
export const voxelKeyFromPoint = (point: Number3, voxelSize: number): string => {
  const [x, y, z] = point;

  const voxelSizeInverse = Math.round(1 / voxelSize);

  const intX = Math.floor(x * voxelSizeInverse);
  const intY = Math.floor(y * voxelSizeInverse);
  const intZ = Math.floor(z * voxelSizeInverse);

  const voxelKey = `${intX}_${intY}_${intZ}`;
  return voxelKey;
};

// large voxel key has coordinates in meters
export const largeVoxelKeyFromPoint = (point: Number3, largeVoxelSize: number): string => {
  const [x, y, z] = point;

  const voxelSizeInverse = 1 / largeVoxelSize;

  const intX = Math.floor(x * voxelSizeInverse) * largeVoxelSize;
  const intY = Math.floor(y * voxelSizeInverse) * largeVoxelSize;
  const intZ = Math.floor(z * voxelSizeInverse) * largeVoxelSize;

  const voxelKey = `${intX}_${intY}_${intZ}`;
  return voxelKey;
};

export const FindViewpointsTool = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const structureId = useGetStructureId();
  const inspectionMetadata = useAtomValue(state.inspectionMetadata);
  const allViewpoints = useAtomValue(state.structureLocations);
  const [showPointMarker, setShowPointMarker] = useAtom(moduleState.showPointMarker);
  const [currentVoxelPoint, setCurrentVoxelPoint] = useAtom(moduleState.currentVoxelPoint);
  const [currentVoxel, setCurrentVoxel] = useAtom(moduleState.currentVoxel);
  const [largeVoxelData, setLargeVoxelData] = useAtom(moduleState.largeVoxelData);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [findViewpointsToolState, setFindViewpointsToolState] = useAtom(
    moduleState.findViewpointsToolState
  );
  const setFoundViewpoints = useSetAtom(moduleState.foundViewpoints);

  const { deactivateFindViewpointsTool, clearFindViewpointsTool } = useFindViewpointsTool();

  const allViewpointsByName = useMemo(() => {
    return new Map(allViewpoints?.map((entry) => [`${entry.deck.name}/${entry.name}`, entry]));
  }, [allViewpoints]);

  const { voxelLocationsPath, voxelSize, largeVoxelSize } = inspectionMetadata ?? {};

  useEffect(() => {
    if (!voxelSize) return;
    if (!largeVoxelSize) return;
    if (!currentVoxelPoint) {
      setCurrentVoxel(undefined);
      setFoundViewpoints([]);
      return;
    }
    setCurrentVoxel({
      voxelKey: voxelKeyFromPoint(currentVoxelPoint, voxelSize),
      largeVoxelKey: largeVoxelKeyFromPoint(currentVoxelPoint, largeVoxelSize),
    });
  }, [currentVoxelPoint, setCurrentVoxel, setFoundViewpoints, voxelSize, largeVoxelSize]);

  useEffect(() => {
    if (!structureId) return;
    if (!currentVoxel?.largeVoxelKey) return;

    const { largeVoxelKey } = currentVoxel;
    if (largeVoxelData?.largeVoxelKey === largeVoxelKey) return;

    setFindViewpointsToolState(FindViewpointsToolState.WAITING_FOR_QUERY);
    setLargeVoxelData(undefined);
    setFoundViewpoints([]);

    const fetchLargeVoxelData = async () => {
      const url = getCloudfrontUrl(`${voxelLocationsPath}${largeVoxelKey}.json`);
      if (!url) return;
      const response = await fetch(url, { method: 'GET', credentials: 'include' });
      if (response.ok) {
        const voxels = await response.json();
        setLargeVoxelData({ voxels, largeVoxelKey });
      }
    };

    fetchLargeVoxelData();
  }, [
    structureId,
    currentVoxel,
    largeVoxelData,
    voxelLocationsPath,
    setFindViewpointsToolState,
    setLargeVoxelData,
    setFoundViewpoints,
  ]);

  useEffect(() => {
    if (!currentVoxel?.largeVoxelKey) return;
    if (currentVoxel?.largeVoxelKey !== largeVoxelData?.largeVoxelKey) return;

    const { voxels } = largeVoxelData;
    const voxelLocationNames = voxels[currentVoxel.voxelKey];

    const viewpoints: moduleState.FoundViewpoints = voxelLocationNames
      .map((name) => (name ? allViewpointsByName.get(name) : undefined) ?? undefined)
      .filter((value) => !!value) as moduleState.FoundViewpoints;

    setFoundViewpoints(viewpoints);
    setFindViewpointsToolState(FindViewpointsToolState.DONE);
  }, [
    currentVoxel,
    largeVoxelData,
    allViewpointsByName,
    setFindViewpointsToolState,
    setFoundViewpoints,
  ]);

  const viewpoints = useAtomValue(moduleState.foundViewpoints);

  useEffect(() => {
    if (!isOpen) {
      setFindViewpointsToolState(FindViewpointsToolState.INACTIVE);
      setFoundViewpoints([]);
      setCurrentVoxelPoint(undefined);
      setCurrentVoxel(undefined);
    }
  }, [
    isOpen,
    setFindViewpointsToolState,
    setFoundViewpoints,
    setCurrentVoxel,
    setCurrentVoxelPoint,
  ]);

  const [drawerRight, setDrawerRight] = useAtom(state.drawerRight);
  const drawerRightPreviousState = useAtomValue(state.drawerRightPreviousState);

  const handleRightToggle = useCallback(
    (property: keyof AnalysisDrawerRight) => {
      setDrawerRight(toggleValue<AnalysisDrawerRight>(drawerRight, property));
    },
    [drawerRight, setDrawerRight]
  );

  const handleCancelClick = useCallback(() => {
    deactivateFindViewpointsTool();
    handleRightToggle(drawerRightPreviousState);
  }, [deactivateFindViewpointsTool, drawerRightPreviousState, handleRightToggle]);

  const handleClearClick = useCallback(() => {
    clearFindViewpointsTool();
  }, [clearFindViewpointsTool]);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  return (
    <SideDrawer
      allDrawersClosed={allClosed}
      isOpen={isOpen}
      handleOpen={handleOpen}
      position="right"
      label="Find viewpoints"
      width={400}
      showCloseButton
      scrollable
      sx={{ position: 'relative' }}
    >
      <Box sx={{ padding: 2 }}>
        {findViewpointsToolState === FindViewpointsToolState.WAITING_FOR_POINT && (
          <Stack
            sx={{ height: 'calc(100vh - 200px)' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <AdsClickIcon sx={{ color: '#7E9BBC', width: '48px', height: '48px' }} />
            <Typography
              variant="body2"
              sx={{ width: '80%', textAlign: 'center', color: '#7E9BBC' }}
            >
              Click on a point in 3d to find viewpoints that can see that point
            </Typography>
          </Stack>
        )}
        {findViewpointsToolState === FindViewpointsToolState.WAITING_FOR_QUERY && (
          <Box display="flex" justifyContent="center" alignItems="center" height={'80vh'}>
            <CircularProgress />
          </Box>
        )}

        {findViewpointsToolState === FindViewpointsToolState.DONE && (
          <Stack>
            <ImageThumbnailHeader>
              <Box sx={{ width: '60%' }}>
                <TextField
                  sx={{ marginTop: 0 }}
                  data-testId="search-field"
                  fullWidth
                  label="Search Viewpoint"
                  placeholder={searchTerm}
                  variant="standard"
                  onChange={handleSearch}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ width: '40%' }}>
                <FormControlLabel
                  sx={{ marginLeft: 2, marginTop: '20px' }}
                  control={
                    <Switch
                      checked={showPointMarker}
                      onChange={() => setShowPointMarker((current) => !current)}
                      size="small"
                    />
                  }
                  label={<Typography style={{ fontSize: '14px' }}>Show Marker</Typography>}
                  labelPlacement="start"
                />
              </Box>
            </ImageThumbnailHeader>
            <ViewpointsTable
              locations={viewpoints}
              lookAt={currentVoxelPoint}
              searchTerm={searchTerm}
            />

            <ImageThumbnailActionButtons>
              <ActionButton
                sx={{ backgroundColor: primary.darkBlue, width: '40%' }}
                variant="contained"
                onClick={handleClearClick}
              >
                Clear
              </ActionButton>
              <ActionButton
                sx={{ color: primary.darkBlue, mr: 2 }}
                variant="outlined"
                onClick={handleCancelClick}
              >
                Cancel
              </ActionButton>
            </ImageThumbnailActionButtons>
          </Stack>
        )}
      </Box>
    </SideDrawer>
  );
};
